// =============================================================================
// Manifesto do app do aluno (PWA) — construído POR ALUNO: o app instalado abre
// direto no portal dele (/s/<slug>) e leva o nome do trainer (white-label).
//
// Servido pela Cloudflare Pages Function em functions/manifest/[slug].ts, na URL
// /manifest/<slug>.webmanifest (URL real e buscável — o Chrome Android precisa
// baixar o manifesto pra gerar o app instalado) e referenciado pelo
// <link rel="manifest"> que src/lib/pwa.ts injeta. Fica FORA de /s/: a rota da
// function vira um prefixo no _routes.json, e sob /s/* ela seria invocada em
// toda página do portal.
//
// Módulo PURO: sem import.meta.env nem DOM — também é importado pela Function.
// Ícones: os neutros de /icons (sem marca Kyra), também no apple-touch-icon do
// iPhone. A logo do trainer NÃO entra: se ela falhar ao baixar (arquivo apagado,
// formato estranho), o Chrome recusa a instalação inteira — e logo com fundo
// transparente ou fora do quadrado fica feia como ícone.
// Slug sempre em minúsculas: é como o Worker busca e pra onde o portal
// redireciona depois do login — o app instalado não pode sair do próprio escopo.
// =============================================================================

export const PORTAL_THEME_COLOR = "#1f1e1d";
export const PORTAL_APP_SHORT_NAME = "Treinos";
export const PORTAL_APP_DESCRIPTION = "Seus treinos e dietas, no seu ritmo.";

export interface ManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose: "any" | "maskable";
}

export interface PortalManifest {
  id: string;
  name: string;
  short_name: string;
  description: string;
  lang: string;
  dir: "ltr";
  start_url: string;
  scope: string;
  display: "standalone";
  background_color: string;
  theme_color: string;
  icons: ManifestIcon[];
}

/** Slug válido do portal (o mesmo formato que o Worker aceita em /portal-info). */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9][a-z0-9_-]{2,63}$/i.test(slug);
}

export function normalizeSlug(slug: string): string {
  return slug.trim().toLowerCase();
}

/** URL do manifesto do aluno (same-origin, servida pela Pages Function). */
export function portalManifestUrl(slug: string): string {
  return `/manifest/${encodeURIComponent(normalizeSlug(slug))}.webmanifest`;
}

/** Slug a partir do segmento da URL do manifesto ("abc123.webmanifest" → "abc123"). */
export function slugFromManifestSegment(segment: string): string {
  return normalizeSlug(segment.trim().replace(/\.webmanifest$/i, ""));
}

/** Nome exibido pelo sistema (tela de instalação, gerenciador de apps). */
export function portalAppName(trainerName: string | null | undefined): string {
  const name = (trainerName ?? "").replace(/\s+/g, " ").trim();
  return name ? `Treinos · ${name}`.slice(0, 60) : "Portal do Aluno";
}

export function buildPortalManifest(slug: string, trainerName: string | null | undefined): PortalManifest {
  const base = `/s/${encodeURIComponent(normalizeSlug(slug))}`;
  return {
    id: base,
    name: portalAppName(trainerName),
    // Rótulo embaixo do ícone (~12 caracteres cabem): curto e do ponto de vista do aluno.
    short_name: PORTAL_APP_SHORT_NAME,
    description: PORTAL_APP_DESCRIPTION,
    lang: "pt-BR",
    dir: "ltr",
    start_url: base,
    scope: base,
    display: "standalone",
    // Sem travar orientação: vídeo de exercício em tela cheia precisa deitar.
    background_color: PORTAL_THEME_COLOR,
    theme_color: PORTAL_THEME_COLOR,
    icons: [
      { src: "/icons/icon-192.png", sizes: "192x192", type: "image/png", purpose: "any" },
      { src: "/icons/icon-512.png", sizes: "512x512", type: "image/png", purpose: "any" },
      { src: "/icons/icon-512.png", sizes: "512x512", type: "image/png", purpose: "maskable" },
    ],
  };
}
